import { defineStore } from "pinia";
import type { TableColumnTypeCustom } from "@/types/claims";
import { computed, ref } from "vue";
import { Claim } from "@/common/claims/claimsEnum";
import type { BaseProductDto } from "@/types/product/productTypes";
import {
  initDefaultColumns,
  useTableColumns,
} from "@/composables/table/tableColumns";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import axios from "axios";
import { createNotification } from "@/tools/notification";
import { getLinks } from "@/tools/createLinkMarketPlace/createLinkToMarketPlace";
import ProductsForConnectinSizes, {
  type ProductsForConnectinSizesFilter,
} from "@/api/classes/productsForConnectinSizes/products-for-connectin-sizes.api";
import { useCustomCategoriesStore } from "@/stores/customCategories/customCategories";

const INITIAL_FILTERS: ProductsForConnectinSizesFilter = {
  searchPhrase: "",
  createdInOzon: null,
  notCreatedInOzon: null,
  createdInWb: null,
  notCreatedInWb: null,
  outOfStockFBS: null,
  outOfStockFBO: null,
  readyToExport: null,
  sortedField: null,
  isOrderByDesc: false,
  categoryId: "",
};

export interface CombineProductsBySizeTableItem {
  image: string;
  name: string;
  size: string;
  links: { link: string; market: string; icon: string }[];
  barcode: string;
  category: string;
  brand: string;
  fbs: number;
  fbo: number;
  id: BaseProductDto["id"];
  actions: null;
}

export const useCombineProductsBySize = defineStore(
  "combineProductsBySizeStore",
  () => {
    const filters = ref({ ...INITIAL_FILTERS });
    const getProductsStatusLoad = ref<enumStatusLoad>(
      enumStatusLoad.NOT_STARTED,
    );
    const customCategoryStore = useCustomCategoriesStore();
    const isLoadingDone = ref<boolean>(false);
    const selectedRowKeys = ref<BaseProductDto["id"][]>([]);

    const isDataLoading = computed(
      () =>
        getProductsStatusLoad.value === enumStatusLoad.LOADING ||
        customCategoryStore.statusLoadGetCustomCategories ===
          enumStatusLoad.LOADING,
    );

    const pagination = ref({
      current: 1,
      total: 500,
      pageSize: 10,
      defaultPageSize: 10,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} из ${total} товаров`,
      onChangePagination: async () => {
        await loadData();
      },
    });
    const productsTableData = ref<CombineProductsBySizeTableItem[]>([]);
    const columnsConfigName = "baskitCombineProductsBySizeTable";

    const combineProductsBySizeColumns = ref<
      TableColumnTypeCustom<CombineProductsBySizeTableItem>[]
    >(
      initDefaultColumns<CombineProductsBySizeTableItem>([
        { dataIndex: "image", title: "Фото", fixed: "left" },
        { dataIndex: "name", title: "Название", fixed: "left" },
        { dataIndex: "size", title: "Размер" },
        { dataIndex: "links", title: "Ссылки на МП" },
        { dataIndex: "barcode", title: "Штрих-код" },
        { dataIndex: "category", title: "Категория" },
        { dataIndex: "brand", title: "Бренд" },
        { dataIndex: "fbs", title: "Остатки FBS всего" },
        { dataIndex: "fbo", title: "Остатки FBO всего" },
        {
          dataIndex: "actions",
          title: "Действия",
          claim: Claim.CanDeleteProducts,
          fixed: "right",
        },
      ]),
    );

    const { columnsFiltered, dragList, initDragList } = useTableColumns(
      combineProductsBySizeColumns,
      columnsConfigName,
      productsTableData,
    );

    initDragList();

    const createTableData = (data: BaseProductDto[]) => {
      const result: CombineProductsBySizeTableItem[] = [];

      data.forEach((product) => {
        const image = product.titleImageLink as string;
        const name = product.title;
        const size = product.size;
        const links = getLinks({
          ozonArticle: product.ozonArticle,
          wbArticle: product.wildberriesArticle,
        });
        const barcode = [...new Set(product.barcode)].join(", ");
        const category = product.category;
        const brand = product.brand;
        const fbo = product.ozonFBO + product.wildberriesFBO;
        const fbs = product.fbsResidues;

        const item: CombineProductsBySizeTableItem = {
          image,
          name,
          size,
          links,
          barcode,
          category,
          brand,
          fbs,
          fbo,
          id: product.id,
          actions: null,
        };

        result.push(item);
      });

      return result;
    };

    const loadData = async (tableFilters = filters.value) => {
      isLoadingDone.value = false;
      try {
        getProductsStatusLoad.value = enumStatusLoad.LOADING;

        const take = pagination.value.pageSize;
        const skip = (pagination.value.current - 1) * pagination.value.pageSize;

        const response = await ProductsForConnectinSizes.getProductsForTable(
          take,
          skip,
          {
            searchPhrase: tableFilters.searchPhrase,
            createdInOzon: tableFilters.createdInOzon
              ? true
              : tableFilters.notCreatedInOzon
                ? false
                : null,
            createdInWb: tableFilters.createdInWb
              ? true
              : tableFilters.notCreatedInWb
                ? false
                : null,
            outOfStockFBS: tableFilters.outOfStockFBS === true ? true : null,
            outOfStockFBO: tableFilters.outOfStockFBO === true ? true : null,
            readyToExport: tableFilters.readyToExport,
            sortedField: tableFilters.sortedField,
            isOrderByDesc: tableFilters.isOrderByDesc,
            categoryId: tableFilters.categoryId,
          },
        );

        const [count, products] = Object.entries(response.data)[0];

        productsTableData.value = createTableData(products);
        pagination.value.total = +count;
        getProductsStatusLoad.value = enumStatusLoad.LOADED;
        updateSelectedItems([]);
      } catch (error) {
        onError(error);
        getProductsStatusLoad.value = enumStatusLoad.ERROR;
      } finally {
        isLoadingDone.value = true;
      }
    };

    const onError = (err: unknown) => {
      if (axios.isAxiosError(err)) {
        createNotification(
          "error",
          err.response?.data?.detail,
          undefined,
          "topRight",
        );
      }
    };

    const updateSelectedItems = (selectedKeys: BaseProductDto["id"][]) => {
      selectedRowKeys.value = [...selectedKeys];
    };

    const clearFilter = () => {
      filters.value = { ...INITIAL_FILTERS };
    };

    return {
      pagination,
      productsTableData,
      combineProductsBySizeColumns,
      columnsFiltered,
      columnsConfigName,
      dragList,
      loadData,
      filters,
      isDataLoading,
      selectedRowKeys,
      updateSelectedItems,
      isLoadingDone,
      clearFilter,
    };
  },
);
